'use client';

import React from 'react';
import {
  ChakraProvider,
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSlotRecipe,
  mergeConfigs
} from '@chakra-ui/react';
import { ColorModeProvider } from './color-mode';

const accordionSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    item: {
      overflowAnchor: 'none',
      borderColor: { _light: 'border', _dark: 'secondary.500' }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'md',
    variant: 'outline'
  }
});
const alertSlotRecipe = defineSlotRecipe({
  variants: {
    status: {
      info: {
        root: { colorPalette: 'blue' }
      },
      warning: {
        root: { colorPalette: 'orange' }
      },
      success: {
        root: { colorPalette: 'green' }
      },
      error: {
        root: { colorPalette: { base: 'red', _dark: 'brand' } }
      },
      neutral: {
        root: { colorPalette: 'gray' }
      }
    },
    variant: {
      subtle: {
        root: {
          bg: 'colorPalette.subtle',
          color: 'colorPalette.fgAlt'
        }
      },
      surface: {
        root: {
          bg: 'colorPalette.subtle',
          color: 'colorPalette.fg',
          shadow: 'inset 0 0 0px 1px var(--shadow-color)',
          shadowColor: 'colorPalette.muted'
        },
        indicator: {
          color: 'colorPalette.fg'
        }
      },
      outline: {
        root: {
          color: 'colorPalette.fg',
          shadow: 'inset 0 0 0px 1px var(--shadow-color)',
          shadowColor: 'colorPalette.muted'
        },
        indicator: {
          color: 'colorPalette.fg'
        }
      },
      solid: {
        root: {
          bg: 'colorPalette.solid',
          color: 'colorPalette.contrast'
        },
        indicator: {
          color: 'colorPalette.contrast'
        }
      }
    }
  },
  defaultVariants: {
    status: 'info',
    variant: 'subtle',
    // @ts-ignore
    size: 'md',
    inline: false
  }
});
const cardSlotRecipe = defineSlotRecipe({
  slots: [],
  variants: {
    variant: {
      elevated: {
        root: {
          bg: 'bg-chart',
          boxShadow: 'sm'
        }
      },
      outline: {
        root: {
          bg: 'bg-chart',
          borderWidth: '1px',
          borderColor: 'border'
        }
      },
      subtle: {
        root: {
          bg: 'bg.muted'
        }
      }
    }
  },
  defaultVariants: {
    variant: 'elevated',
    // @ts-ignore
    size: 'md'
  }
});
const dialogSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    content: {
      bg: { _light: 'bg.panel', _dark: 'secondary.700' }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'md',
    // @ts-ignore
    scrollBehavior: 'outside',
    placement: 'top',
    motionPreset: 'scale'
  }
});
const drawerSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    content: {
      bg: { _light: 'bg.panel', _dark: 'secondary.700' }
    }
  }
});
const menuSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    content: {
      bg: 'popup-bg'
    },
    separator: {
      height: '1px',
      borderColor: { _light: 'gray.100', _dark: 'secondary.400' },
      my: '1',
      mx: '-1'
    }
  },
  variants: {
    variant: {
      subtle: {
        item: {
          _highlighted: {
            borderRadius: 'sm',
            bg: { _light: 'bg.muted', _dark: 'secondary.400' }
          }
        }
      }
    },
    size: {
      md: {
        content: {
          minW: '8rem',
          padding: '1.5'
        },
        item: {
          gap: '2',
          textStyle: 'sm',
          py: '2',
          px: '2'
        }
      }
    }
  },

  defaultVariants: {
    size: 'md',
    variant: 'subtle'
  }
});
const segmentGroupSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    root: {
      bg: { _light: 'bg.muted', _dark: 'popup-bg' }
    },

    indicator: {
      bg: { _light: 'bg', _dark: 'secondary.muted' }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'md'
  }
});
const selectSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    content: {
      background: { _light: 'bg.panel', _dark: 'popup-bg' }
    },
    item: {
      _highlighted: {
        bg: { _light: 'bg.muted', _dark: 'secondary.muted' }
      }
    },
    itemGroupLabel: {
      color: { _light: 'secondary.700', _dark: 'secondary.100' },
      fontWeight: 'medium'
    },
    itemIndicator: {
      bg: 'transparent',
      color: { _light: 'brand.500', _dark: 'brand.600' }
    }
  },
  variants: {
    variant: {
      outline: {
        trigger: {
          borderWidth: '1px',
          borderColor: { _light: 'border', _dark: 'secondary.500' },
          bg: 'bg-input',
          focusRing: 'inside',
          _expanded: {
            borderColor: 'border.emphasized'
          }
        }
      },
      subtle: {
        trigger: {
          borderWidth: '1px',
          borderColor: 'transparent',
          bg: 'bg-input'
        }
      }
    }
  },

  defaultVariants: {
    // @ts-ignore
    size: 'md',
    variant: 'outline',
    // @ts-ignore
    colorPalette: 'brand'
  }
});
const comboboxSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    content: {
      background: { _light: 'bg.panel', _dark: 'popup-bg' }
    },
    item: {
      _highlighted: {
        bg: { _light: 'bg.muted', _dark: 'secondary.muted' }
      }
    },
    itemGroupLabel: {
      color: { _light: 'secondary.700', _dark: 'secondary.100' },
      fontWeight: 'medium'
    },
    itemIndicator: {
      bg: 'transparent',
      color: { _light: 'brand.500', _dark: 'brand.600' }
    }
  },
  variants: {
    variant: {
      outline: {
        input: {
          borderWidth: '1px',
          borderColor: { _light: 'border', _dark: 'secondary.500' },
          bg: 'bg-input',
          focusVisibleRing: 'inside'
        }
      },
      subtle: {
        input: {
          borderWidth: '1px',
          borderColor: 'transparent',
          bg: 'bg-input',
          focusVisibleRing: 'inside'
        }
      }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'md',
    variant: 'outline',
    // @ts-ignore
    colorPalette: 'brand'
  }
});
const separatorRecipe = defineRecipe({
  variants: {
    variant: {
      solid: {
        borderStyle: 'solid',
        borderColor: { _light: 'border', _dark: 'secondary.500' }
      }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'sm',
    variant: 'solid',
    orientation: 'horizontal'
  }
});
const skeletonRecipe = defineRecipe({
  variants: {
    variant: {
      pulse: {
        background: { _light: 'bg.emphasized', _dark: 'secondary.400' },
        animation: 'pulse',
        animationDuration: 'var(--duration, 1.2s)'
      }
    }
  },
  defaultVariants: {
    variant: 'pulse',
    // @ts-ignore
    loading: true
  }
});
const statSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1',
      position: 'relative',
      flex: '1'
    },
    label: {
      color: 'fg.muted',
      textStyle: 'sm'
    },
    helpText: {
      color: 'fg.muted',
      textStyle: 'xs'
    },
    valueUnit: {
      color: 'fg.muted',
      textStyle: 'xs',
      fontWeight: 'initial',
      letterSpacing: 'initial'
    },
    valueText: {
      verticalAlign: 'baseline',
      fontWeight: 'semibold',
      letterSpacing: 'tight',
      fontFeatureSettings: 'pnum',
      fontVariantNumeric: 'proportional-nums',
      display: 'inline-flex',
      gap: '1'
    },
    indicator: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginEnd: 1,
      '& :where(svg)': {
        w: '1em',
        h: '1em'
      },
      '&[data-type=up]': {
        color: 'currentColor'
      },
      '&[data-type=down]': {
        color: 'currentColor'
      }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'md'
  }
});
const switchSlotRecipe = defineSlotRecipe({
  slots: [],
  variants: {
    variant: {
      solid: {
        control: {
          bg: { _light: 'bg.emphasized', _dark: 'secondary.muted' },
          _checked: {
            bg: { _light: 'colorPalette.solid', _dark: 'colorPalette.solid' }
          }
        },
        thumb: {
          bg: 'white',
          _checked: {
            bg: 'white'
          }
        }
      }
    }
  },
  defaultVariants: {
    variant: 'solid',
    // @ts-ignore
    size: 'md'
  }
});
const tableSlotRecipe = defineSlotRecipe({
  slots: [],
  variants: {
    variant: {
      line: {
        columnHeader: {
          borderColor: { _light: 'gray.200', _dark: 'popup-table-border' },
          borderBottomWidth: '2px',
          color: { _light: 'gray.500', _dark: 'secondary.100' }
        },
        cell: {
          borderColor: { _light: 'gray.200', _dark: 'popup-table-border' },
          borderBottomWidth: '1px'
        },
        row: {
          bg: 'transparent'
        }
      }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'md',
    // @ts-ignore
    variant: 'line'
  }
});
const tabsSlotRecipe = defineSlotRecipe({
  slots: [],
  variants: {
    variant: {
      line: {
        list: {
          borderColor: { _light: 'border', _dark: 'secondary.500' },
          _horizontal: {
            borderBottomWidth: '1px'
          },
          _vertical: {
            borderEndWidth: '1px'
          }
        },
        trigger: {
          color: 'fg.muted',
          _selected: {
            color: 'fg'
          }
        }
      }
    }
  },

  defaultVariants: {
    // @ts-ignore
    size: 'lg',
    variant: 'line'
  }
});
const toastSlotRecipe = defineSlotRecipe({
  slots: [],
  base: {
    root: {
      bg: { _light: 'secondary.500', _dark: 'secondary.400' },
      color: { _light: 'brand.contrast', _dark: 'gray.50' },
      '--toast-trigger-bg': 'colors.bg.muted',
      '&[data-type=warning]': {
        bg: 'orange.solid',
        color: 'orange.contrast'
      },
      '&[data-type=success]': {
        bg: { _light: 'secondary.500', _dark: 'secondary.400' },
        color: { _light: 'brand.contrast', _dark: 'gray.50' }
      },
      '&[data-type=error]': {
        bg: 'brand.700',
        color: 'red.contrast'
      }
    }
  }
});
const buttonRecipe = defineRecipe({
  variants: {
    variant: {
      solid: {
        bg: 'colorPalette.solid',
        color: 'colorPalette.contrast',
        _hover: {
          bg: 'colorPalette.solid/90'
        },
        _expanded: {
          bg: 'colorPalette.solid/90'
        },
        colorPalette: 'brand'
      },
      ghost: {
        _hover: {
          bg: { _light: 'gray.100', _dark: 'secondary.500' }
        }
      },
      outline: {
        _hover: {
          bg: { _light: 'gray.100', _dark: 'secondary.500' }
        }
      }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'lg',
    variant: 'solid'
  }
});
const inputRecipe = defineRecipe({
  variants: {
    variant: {
      outline: {
        borderWidth: '1px',
        borderColor: { _light: 'border', _dark: 'secondary.500' },
        bg: 'bg-input',
        focusVisibleRing: 'inside',
        colorPalette: 'brand'
      },
      subtle: {
        borderWidth: '1px',
        borderColor: 'transparent',
        bg: 'bg-input',
        focusVisibleRing: 'inside',
        colorPalette: 'brand'
      }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'md',
    variant: 'outline'
  }
});

const textareaRecipe = defineRecipe({
  variants: {
    variant: {
      outline: {
        borderWidth: '1px',
        borderColor: { _light: 'border', _dark: 'secondary.500' },
        bg: 'bg-input',
        focusVisibleRing: 'inside',
        colorPalette: 'brand'
      },
      subtle: {
        borderWidth: '1px',
        borderColor: 'transparent',
        bg: 'bg-input',
        focusVisibleRing: 'inside',
        colorPalette: 'brand'
      }
    }
  },
  defaultVariants: {
    // @ts-ignore
    size: 'md',
    variant: 'outline'
  }
});
const theme = defineConfig({
  theme: {
    tokens: {
      fontWeights: {
        bold: { value: '600' }
      },
      colors: {
        brand: {
          exact500: { value: '#fe504f' },
          exact400: { value: '#6BA8B0' },
          exact300: { value: '#87C2C9' },

          50: { value: '#fffbfb' },
          100: { value: '#fe9695' },
          200: { value: '#fe8584' },
          300: { value: '#fe7372' },
          400: { value: '#fe6261' },
          500: { value: '#fe504f' },
          600: { value: '#e54847' },
          700: { value: '#cb403f' },
          800: { value: '#b23837' },
          900: { value: '#98302f' }
        },
        secondary: {
          DEFAULT: { value: '#0C0042' }, // Main color
          // DEFAULT: { value: '#1b222c' }, // Main color
          50: { value: '#a8b1bb' },
          100: { value: '#8f9ba8' },
          200: { value: '#768595' },
          300: { value: '#5d6f82' },
          400: { value: '#44596f' },
          500: { value: '#212c38' }, // Your specified lighter shade
          600: { value: '#1b222c' }, // Main color
          700: { value: '#171f27' },
          800: { value: '#0f1927' }, // Your specified darker shade
          900: { value: '#0a1118' }
        },
        black: { value: '#262626' },
        black300: { value: '#475C6E' },
        offWhite: { value: '#ECF0F0' },
        offWhite500: { value: '#DCDDD9' },
        offWhite600: { value: '#ABA9AC' }
      }
    },
    semanticTokens: {
      colors: {
        brand: {
          solid: { value: { base: 'colors.brand.500', _dark: 'colors.brand.400' } },
          contrast: { value: { base: 'colors.brand.50', _dark: 'colors.secondary' } },
          fg: { value: { base: '{colors.brand.600}', _dark: '{colors.brand.300}' } },
          fgAlt: { value: { base: '{colors.brand.900}', _dark: '{colors.brand.100}' } },
          muted: { value: '{colors.brand.100}' },
          subtle: { value: { base: '{colors.brand.100}', _dark: '{colors.brand.900}' } },
          emphasized: { value: '{colors.brand.300}' },
          focusRing: { value: '{colors.brand.500}' }
        },
        secondary: {
          solid: { value: { base: 'colors.secondary.500', _dark: 'colors.secondary.500' } },
          contrast: { value: { base: 'colors.secondary.50', _dark: 'colors.secondary.900' } },
          fg: { value: { base: '{colors.secondary.600}', _dark: '{colors.gray.200}' } },
          fgAlt: { value: { base: '{colors.secondary.900}', _dark: '{colors.gray.200}' } },
          muted: { value: { base: '{colors.secondary.100}', _dark: '{colors.secondary.400}' } },
          subtle: { value: { base: '{colors.gray.200}', _dark: '{colors.secondary.900}' } },
          emphasized: { value: '{colors.secondary.300}' },
          focusRing: { value: '{colors.secondary.500}' }
        },
        'fg.subtle': { value: { base: 'colors.gray.400', _dark: 'colors.secondary.300' } },
        'fg.muted': { value: { base: 'colors.gray.500', _dark: 'colors.secondary.100' } },
        accent: {
          value: { base: 'colors.brand.500', _dark: 'colors.brand.400' }
        },
        'accent-alt': {
          value: { base: 'colors.secondary.900', _dark: 'colors.white' }
        },
        'bg-input': {
          value: { base: 'colors.white', _dark: 'colors.secondary.500' }
        },
        'bg-default': {
          value: { base: 'colors.gray.50', _dark: 'colors.secondary.600' }
        },
        'input-radio-accent': {
          value: { base: 'colors.gray.200', _dark: 'colors.secondary.400' }
        },
        'bg-surface': {
          value: { base: 'colors.white', _dark: 'colors.secondary.500' }
        },
        'bg-banner': {
          value: { base: 'colors.secondary.900', _dark: 'colors.secondary.500' }
        },
        'bg-chart': {
          value: { base: 'colors.white', _dark: 'colors.secondary.500' }
        },
        'status-success-bg': {
          value: { base: 'colors.purple.100', _dark: 'colors.purple.800' }
        },
        'status-success-fg': {
          value: { base: 'colors.purple.800', _dark: 'colors.purple.200' }
        },
        'status-rejected-bg': {
          value: { base: 'rgba(254,80,79,0.12)', _dark: 'rgba(254,80,79,0.22)' }
        },
        'status-rejected-fg': {
          value: { base: 'colors.brand.900', _dark: 'colors.brand.100' }
        },
        'status-paid-bg': {
          value: { base: 'colors.purple.300', _dark: 'colors.purple.300' }
        },
        'status-paid-fg': {
          value: { base: 'colors.purple.900', _dark: 'colors.purple.900' }
        },
        'stat-positive-bg': {
          value: { base: 'colors.purple.100', _dark: 'colors.purple.800' }
        },
        'stat-positive-fg': {
          value: { base: 'colors.purple.800', _dark: 'colors.purple.200' }
        },
        'stat-negative-bg': {
          value: { base: 'colors.red.100', _dark: 'colors.red.800' }
        },
        'stat-negative-fg': {
          value: { base: 'colors.red.700', _dark: 'colors.red.200' }
        },
        'status-pending-bg': {
          value: { base: 'colors.yellow.100', _dark: 'colors.yellow.800' }
        },
        'status-pending-fg': {
          value: { base: 'colors.yellow.900', _dark: 'colors.yellow.100' }
        },
        'status-neutral-bg': {
          value: { base: 'colors.gray.200', _dark: 'colors.gray.600' }
        },
        'status-neutral-fg': {
          value: { base: 'colors.gray.800', _dark: 'colors.gray.100' }
        },
        'bg-code': {
          value: { base: '#F7F7FA', _dark: '#0F1419' }
        },
        'border-code': {
          value: { base: '#E3E3EC', _dark: '#1F2937' }
        },
        'fg-code': {
          value: { base: '#1B222C', _dark: '#E6E8EB' }
        },
        'fg-code-keyword': {
          value: { base: '#0C0042', _dark: '#C5B8FF' }
        },
        'fg-code-string': {
          value: { base: '#B23A1A', _dark: '#FFB38A' }
        },
        'fg-code-number': {
          value: { base: '#2F855A', _dark: '#A6E3A1' }
        },
        'fg-code-comment': {
          value: { base: '#6B7280', _dark: '#94A3B8' }
        },
        'fg-code-attr': {
          value: { base: '#7A4CC4', _dark: '#C7A6FF' }
        },
        'bg-mark': {
          value: { base: 'rgba(254,80,79,0.12)', _dark: 'rgba(254,80,79,0.28)' }
        },
        'bg-mark-active': {
          value: { base: 'rgba(254,80,79,0.28)', _dark: 'rgba(254,80,79,0.5)' }
        },
        'color-inverse': {
          value: { base: 'colors.white', _dark: 'colors.secondary.800' }
        },
        'chart-color1': {
          value: { base: 'colors.purple.500', _dark: 'colors.purple.400' }
        },
        'chart-color2': {
          value: { base: 'colors.orange.300', _dark: 'colors.orange.400' }
        },
        'chart-color3': {
          value: { base: 'colors.brand.300', _dark: 'colors.brand.400' }
        },
        'chart-color4': {
          value: { base: 'colors.purple.300', _dark: 'colors.purple.300' }
        },
        'chart-color5': {
          value: { base: 'colors.brand.100', _dark: 'colors.brand.100' }
        },
        heading: {
          value: { base: 'colors.secondary.500', _dark: 'colors.gray.100' }
        },
        'popup-bg': {
          value: { base: 'colors.white', _dark: 'colors.secondary.500' }
        },
        'popup-input': {
          value: { base: 'colors.gray.100', _dark: 'colors.secondary.600' }
        },
        'popup-table-border': {
          value: { base: 'colors.gray.100', _dark: 'colors.secondary.400' }
        },
        'native-select': {
          value: { base: 'colors.white', _dark: 'colors.secondary.500' }
        },
        'table-header': {
          value: { base: 'colors.gray.500', _dark: 'colors.secondary.100' }
        },
        'table-header-hover': {
          value: { base: 'colors.gray.100', _dark: 'colors.secondary.600' }
        },
        icon: {
          value: { base: 'colors.brand.500', _dark: 'colors.brand.400' }
        }
      }
    },
    recipes: {
      button: buttonRecipe,
      input: inputRecipe,
      separator: separatorRecipe,
      skeleton: skeletonRecipe,
      textarea: textareaRecipe
    },
    slotRecipes: {
      accordion: accordionSlotRecipe,
      alert: alertSlotRecipe,
      card: cardSlotRecipe,
      dialog: dialogSlotRecipe,
      drawer: drawerSlotRecipe,
      menu: menuSlotRecipe,
      combobox: comboboxSlotRecipe,
      segmentGroup: segmentGroupSlotRecipe,
      select: selectSlotRecipe,
      stat: statSlotRecipe,
      switch: switchSlotRecipe,
      table: tableSlotRecipe,
      tabs: tabsSlotRecipe,
      toast: toastSlotRecipe
    }
  },
  globalCss: {
    '.chakra-native-select__field': {
      backgroundColor: 'var(--eezi-native-select-bg, var(--chakra-colors-native-select))!important',
      color: 'fg'
    }
    //   'chakra-native-select__field::-webkit-scrollbar-track': {
    //     backgroundColor: 'transparent'
    //   }
    // 'chakra-native-select__field::- webkit - scrollbar - thumb': {
    //     background: #3182ce;
    //     borderRadius: '4px';
    // }
    // }
  }
});
const config = mergeConfigs(defaultConfig, theme);
const system = createSystem(config, theme);

export const Provider = ({ children }: React.PropsWithChildren) => (
  <ChakraProvider value={system}>
    <ColorModeProvider>{children}</ColorModeProvider>
  </ChakraProvider>
);
